import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';

import { qk } from '../../lib/query-keys';
import { fetchAuthSession } from './api';
import { decideSession, shouldRetrySession } from './session-decision';

/** يقرؤه `/login` بعد نجاح الدخول فيعيد المالك إلى ما كان يقصده. */
export const INTENDED_PATH_KEY = 'flow.intended-path';

export function takeIntendedPath(): string | null {
  const value = window.sessionStorage.getItem(INTENDED_PATH_KEY);
  if (value !== null) window.sessionStorage.removeItem(INTENDED_PATH_KEY);
  return value;
}

/**
 * حارس الجلسة.
 *
 * الحدّ الأمني الفعلي هو الخادم: كل نقطة في `/api/v1` ترفض بـ `401` بلا اعتماد،
 * فلا تتسرّب بيانة واحدة بغياب هذا الحارس. لكن رسم القشرة كاملةً بلا جلسة **يكذب
 * على الزائر**: يرى شريطًا جانبيًا و«صباح النور» فيظن نفسه داخلًا، ثم يقابله صف
 * رسائل خطأ لا يفهم سببها. فالحارس هنا **صدقُ واجهةٍ** لا طبقة أمن ثانية.
 *
 * القراءة تمر بـ TanStack Query فتُشارَك مع بقية الشاشات بلا نداء ثانٍ.
 *
 * **و`401` وحدها تعني «لست داخلًا».** كان `!isError` يجعل أي خطأ طردًا إلى
 * `/login`: ردّ 429 عند دفقةٍ من الطلبات، أو 502 أثناء إعادة تشغيل، أو انقطاعٌ
 * لحظيّ — كلها كانت تُخرج المالك وجلسته سليمة وتطالبه بكلمة مروره. والقرار
 * صار دالّةً نقيّة في `session-decision.ts` باختباره.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  const { data, isPending, error } = useQuery({
    queryKey: qk.authSession(),
    queryFn: fetchAuthSession,
    retry: shouldRetrySession,
    staleTime: 60_000,
  });

  const decision = decideSession({ isPending, error, authenticated: data?.authenticated });
  const authenticated = decision.kind === 'authenticated';

  useEffect(() => {
    if (decision.kind !== 'unauthenticated') return;
    // المقصد يُحفظ في `sessionStorage` لا في معامل بحث: عقد مسار `/login` بلا
    // معاملات، وإضافة واحد لأجل هذا وحده تُثقل العقد على كل مستدعٍ.
    const intended = window.location.pathname + window.location.search;
    if (intended !== '/' && !intended.startsWith('/login')) {
      window.sessionStorage.setItem(INTENDED_PATH_KEY, intended);
    }
    void navigate({ to: '/login', replace: true });
  }, [decision.kind, navigate]);

  /**
   * خطأٌ ليس 401: **لا طرد ولا رسم**. القشرة تفتح طلباتها فتفشل بدورها، وشاشة
   * الدخول تكذب على مالكٍ جلستُه سليمة. فيبقى مكانه ويُقال له ما حدث.
   */
  if (decision.kind === 'unavailable') {
    return (
      <div className="grid min-h-dvh place-items-center bg-bg px-6">
        <p role="status" className="text-body text-text-secondary">
          {decision.messageAr}
        </p>
      </div>
    );
  }

  /**
   * أثناء الفحص لا تُرسم القشرة ولا شاشة تحميل صاخبة: خلفية ساكنة بلون الصفحة.
   * وميض شريط جانبي ثم اختفاؤه أسوأ من سكون قصير.
   */
  if (!authenticated) {
    return <div className="min-h-dvh bg-bg" aria-busy={isPending} />;
  }

  return <>{children}</>;
}
